import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRightLeft, Bell, Info, Loader } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

const CATEGORY_ICONS = {
  waiver_awarded:  { Icon: Check,           color: 'text-green-400'  },
  waiver_denied:   { Icon: X,               color: 'text-red-400'    },
  trade_received:  { Icon: ArrowRightLeft,  color: 'text-blue-400'   },
  trade_accepted:  { Icon: Check,           color: 'text-green-400'  },
  trade_rejected:  { Icon: X,               color: 'text-red-400'    },
  trade_cancelled: { Icon: X,               color: 'text-orange-400' },
  trade_approved:  { Icon: Check,           color: 'text-green-400'  },
  trade_vetoed:    { Icon: X,               color: 'text-red-400'    },
  system:          { Icon: Info,            color: 'text-gray-400'   },
};

function formatTime(isoStr) {
  if (!isoStr) return '';
  try {
    const dt = new Date(isoStr);
    const now = new Date();
    const diffMs = now - dt;
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays  = diffMs / (1000 * 60 * 60 * 24);
    if (diffHours < 1) {
      const mins = Math.floor(diffMs / 60000);
      return `${mins}m ago`;
    }
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    if (diffDays  < 2)  return 'Yesterday';
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

const NotificationDropdown = ({ style }) => {
  const { notifications, loading, toggleOpen, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        toggleOpen();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [toggleOpen]);

  const handleRowClick = (notif) => {
    markRead(notif.notification_id);
    if (notif.notification_data.action_url) {
      toggleOpen();
      navigate(notif.notification_data.action_url);
    }
  };

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="fixed z-[99999] w-80 rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden"
      style={style}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-700">
        <span className="font-semibold text-white text-sm">Notifications</span>
        <div className="flex items-center gap-3">
          <button
            onClick={markAllRead}
            className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            Mark all read
          </button>
          <button
            onClick={toggleOpen}
            className="text-neutral-400 hover:text-white transition-colors"
            aria-label="Close notifications"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-5 h-5 text-yellow-400 animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8 text-center text-neutral-400 text-sm">
            No new notifications
          </div>
        ) : (
          notifications.slice(0, 50).map((notif) => {
            const nd = notif.notification_data;
            const { Icon, color } = CATEGORY_ICONS[nd.type] || CATEGORY_ICONS[nd.category] || { Icon: Bell, color: 'text-neutral-400' };
            const truncated = nd.message.length > 80 ? `${nd.message.slice(0, 80)}...` : nd.message;
            // Bible § 4.5.12.1 RULE 3: a notification with no action_url has no
            // destination — render it visibly NON-clickable (default cursor, no
            // hover-as-link). Clicking still marks-read, but it must not LOOK navigable.
            const hasAction = !!nd.action_url;

            return (
              <button
                key={notif.notification_id}
                onClick={() => handleRowClick(notif)}
                className={`w-full text-left px-4 py-3 border-b border-neutral-800 flex items-start gap-3 transition-colors ${
                  hasAction ? 'cursor-pointer' : 'cursor-default'
                } ${
                  notif.is_read
                    ? `opacity-50 ${hasAction ? 'hover:opacity-70 hover:bg-neutral-800/30' : ''}`
                    : hasAction ? 'hover:bg-neutral-800/60' : ''
                }`}
              >
                <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-neutral-400">{truncated}</p>
                  <p className="text-[10px] text-neutral-500 mt-1">
                    {formatTime(notif.created_at)}
                  </p>
                </div>
                {!notif.is_read && (
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0 mt-1.5" />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-neutral-700">
        <p className="text-[10px] text-neutral-500 text-center">
          Showing last 50 notifications
        </p>
      </div>
    </div>,
    document.body
  );
};

export default NotificationDropdown;
